/**
 * Coaching lines for the guided first round, and for the two moments that
 * ambush a player long after it.
 *
 * A modal tour of the UI is the thing everybody skips, and it costs us the
 * "playable in 15 seconds" target. So the tutorial IS a match: one offline
 * practice match with a single plain-language line at each beat of the loop.
 * You learn bank-or-push by doing it once, with the stakes at zero.
 *
 * These lines are PRESENTATION ONLY. Nothing here reads or writes match state,
 * and nothing in the match branches on being coached — a coached match is
 * scored, timed and driven exactly like any other practice match. If that ever
 * stops being true, the tutorial has started teaching a game we do not ship.
 *
 * TWO KINDS OF BEAT, and the difference is what they are for.
 *
 * The LOOP beats — pinpoint, decision, probe, round-end — teach the loop, so
 * they run on round one of a guided match and then shut up. By round two they
 * are noise, and a tutorial that will not shut up is worse than none.
 *
 * The ONE-SHOT beats do not belong to the tutorial at all. A golden round and
 * a busted pot are rare, they can land in any match at any round, and they are
 * the two moments a new player most needs a sentence:
 *
 *   - The golden round pays triple off an OPEN answer with no options. Meeting
 *     that cold, mid-match, at a x9 push, reads as the game having changed the
 *     rules on you.
 *   - Losing a pot is the moment somebody decides whether this game is
 *     exciting or unfair. Nothing on screen spoke to them; the round simply
 *     ended at zero. That is the churn moment, and it was silent.
 *
 * So they fire ONCE EVER, in any match, guided or not — the caller holds the
 * once-ness (see `store/storage.ts`), because it has to outlive the tab.
 */

import { SCORING } from '@geogame/shared';

export type CoachBeat =
  | 'pinpoint'
  | 'decision'
  | 'probe'
  | 'round-end'
  | 'golden-armed'
  | 'bust';

/** Beats that teach the loop. Round one of a guided match, and nowhere else. */
export const LOOP_BEATS: readonly CoachBeat[] = ['pinpoint', 'decision', 'probe', 'round-end'];

/** Beats that fire once ever, on their own trigger, in any match. */
export const ONE_SHOT_BEATS: readonly CoachBeat[] = ['golden-armed', 'bust'];

export function isOneShot(beat: CoachBeat): boolean {
  return ONE_SHOT_BEATS.includes(beat);
}

export interface CoachLine {
  readonly kicker: string;
  readonly body: string;
}

/**
 * @param beat  Which moment of the round we are in.
 * @param ctx   The few live numbers the line is allowed to quote back.
 */
export function coachLine(
  beat: CoachBeat,
  ctx: {
    readonly roundIdx: number;
    readonly attemptsLeft: number | null;
    readonly potNow: number;
    readonly potIfPushed: number;
    readonly cityMode: boolean;
    /** The subdivision mode, whose rung 2 is answered on the globe too. */
    readonly stateMode?: boolean;
    /** What the pot was worth at the moment it was lost. For the bust line. */
    readonly potLost?: number;
    /** Points already banked and therefore untouchable. For the bust line. */
    readonly bankedTotal?: number;
  },
): CoachLine | null {
  // Coach the loop on the FIRST round only. The one-shot beats are exempt:
  // they are not teaching the loop, and the moments they exist for cannot be
  // made to happen on round one.
  if (ctx.roundIdx > 0 && !isOneShot(beat)) return null;

  switch (beat) {
    case 'pinpoint':
      return {
        kicker: 'Step 1 of 2',
        body:
          ctx.attemptsLeft !== null && ctx.attemptsLeft > 1
            ? `Tap the globe where you think it is. You get ${ctx.attemptsLeft} tries at this tier, and each one still pays. Take your time, because this round is not on the real clock.`
            : ctx.cityMode
              ? 'Tap the globe where you think it is. You are scored on how close you land, so a near miss still pays. Take your time, because this round is not on the real clock.'
              : 'Tap the globe where you think it is. Take your time, because this round is not on the real clock.',
      };

    case 'decision':
      return {
        kicker: 'Step 2 of 2: this is the whole game',
        body: `Bank keeps ${ctx.potNow.toLocaleString()} for good. Push bets all of it on one QUESTION about the place you just found, never on finding it again, for ${ctx.potIfPushed.toLocaleString()}. Miss and you keep nothing. There is no wrong choice here.`,
      };

    case 'probe':
      return {
        kicker: 'You pushed',
        body: ctx.stateMode
          ? 'This one is answered on the map: four of the state’s cities, and one of them is the capital. Tap it before the ring runs out.'
          : 'Answer before the ring runs out. Right, and the pot grows and you get the same choice again. Wrong, and the round ends at zero.',
      };

    /*
     * SAYS THE CLOCK CHANGES, because it does. Round one runs on the slow
     * tutorial clock (`roundClockMs`) and every round after it runs at the
     * tier's real pace. A player who was not told would meet a 15s rung
     * believing they had 60, lose a round to it, and conclude the game had
     * cheated them — which is a worse first impression than the one the slow
     * clock was added to fix.
     */
    case 'round-end':
      return {
        kicker: "That's the loop",
        body: 'That round ran slow so you could read. The rest go at full speed. The clock is much shorter from here, and the lines stop.',
      };

    /*
     * Fired on the ARM card, one lead-time before the prompt, so the warning
     * lands while there is still time to plan rather than as a caption on a
     * question already running.
     */
    case 'golden-armed':
      return {
        kicker: 'Golden round, once per match',
        body: `The last question on this round pays ${SCORING.GOLDEN_MULTIPLIER_FACTOR}x, and it is an OPEN answer. You type a number, and there are no options to pick from. Everything below it is an ordinary round. Climb it or bank early; both are respectable.`,
      };

    /*
     * The line a player is owed the first time a push costs them everything.
     *
     * It says the two things that are actually true and were nowhere on
     * screen: banked points cannot be taken back, and the next round starts
     * level. Without those, losing a pot reads as the game punishing you
     * permanently for playing it the way it asked you to.
     */
    case 'bust': {
      const lost = ctx.potLost ?? 0;
      const banked = ctx.bankedTotal ?? 0;
      // The amount is quoted only when it is KNOWN. The local machine zeroes
      // the pot the moment it busts, so there is a beat before the server's
      // summary lands where the honest answer is "the pot" rather than "0".
      const cost = lost > 0 ? `${lost.toLocaleString()} gone` : 'the whole pot gone';
      const safe =
        banked > 0
          ? `The ${banked.toLocaleString()} you already banked is safe and always will be.`
          : 'Anything you bank is safe and always will be.';
      return {
        kicker: 'You lost the pot',
        body: `That is the bet: ${cost}. ${safe} The next round starts fresh, and banking earlier is how you keep more of it.`,
      };
    }

    default:
      return null;
  }
}
